import {
  popupStore,
  userInfoStore,
  roundInfoStore,
  gameResultStore,
  gameConfigStore,
  betStore,
  profileAvatarStore,
} from '@pascal/pascal-core/shared/core/store';

import { userSettingsStore } from '@pascal/pascal-core/shared/core/store/userSettingsStore';
import {
  parseQuery,
  RoundStatus,
  applicationStore,
  DefaultQueryParams,
  GameModes,
  notificationStore,
  BetResponseCode,
  BetStatus,
  parseNumberByPrecision,
  iframeService,
  freeBetStore,
  FreeBetWinningType,
  loadingStore,
} from '@pascal/pascal-core';
import { doBetStore, gameStore, marketsStore } from '../store';
import { parseDataFromLiveBets } from '../helpers';
import { autoBetDataKeys, autoCashOutDataKeys, oddballBetShopGameId } from '../enums';

import { worker } from '../index';
import { liveConnection } from '../core';
import { gameActiveVideoStore } from '../store/gameActiveVideoStore';
import { gameLiveConnection } from './GameConnection';
import { soundService, soundTypes } from './soundService';
import { resultStore } from '../store/terminalStore';
import { resultStore as betShopResultStore } from '../store/betShopStore/resultsHistoryStore';
import { reportsStore } from '../store/betShopStore/reportsStore';
import { gameSceneStore } from '../store/betShopStore/gameSceneStore';
import { divide, minus, multiply, toFixed } from '../helpers/arithmeticOperations';

let timeOutId;

class GameService {
  onGetInitialStateResult({
    rate,
    limits,
    results,
    balance,
    avatarId,
    precision,
    roundInfo,
    currencyCode,
    currentBets,
    underMaintenance,
    gameConfiguration,
  }) {
    const { OnHold, Started, Finished, OnFinish } = RoundStatus;
    const { status, second, odd } = roundInfo;
    const { [DefaultQueryParams.Mode]: mode, [DefaultQueryParams.GameId]: gameId } = parseQuery(
      window.location.search
    );

    gameStore.resetGameByGameTypeId();

    const initialBetAmount = freeBetStore?.activeFreeBet.id
      ? freeBetStore?.activeFreeBet?.amount
      : limits?.defaultBetAmount || limits?.minBet;
    const isBetShop = +gameId === oddballBetShopGameId;
    if (isBetShop) {
      this.betShopGetInitial({ results, roundInfo });
    } else {
      if (underMaintenance) {
        const { startDate, notifySeconds } = underMaintenance;
        popupStore.startUnderMaintenanceFunctional({ startDate, notifySeconds });
      }

      gameStore.setGameState({
        status,
        isInitialLoaded: true,
      });

      gameConfigStore.setGameConfiguration(gameConfiguration);

      if (status === Started) {
        const isBetClosed = second < 0;
        gameActiveVideoStore.resetActiveGameVideo();

        if (isBetClosed) {
          gameActiveVideoStore.setGameKick(true);
          gameStore.setIsBetsClosed(true);
          soundService.play(soundTypes.start);
        } else {
          gameActiveVideoStore.setGameStart(true);
        }

        doBetStore.setWinAmount('');
        gameStore.setFinishedOdd(null);
        marketsStore.updateIsDisabledMarketBetButton(false);
        marketsStore.setIsDisabledMarketsSection(false);
        marketsStore.setTotalWin('');
        betStore.setLiveBets([]);

        marketsStore.setBetsMarketData(null);
      } else if (status === OnHold) {
        gameActiveVideoStore.setGameProcess(true);
        gameStore.setFinishedOdd(null);
      } else if (status === Finished || status === OnFinish) {
        gameActiveVideoStore.setGameFinish(true);
        if (currentBets && currentBets.length) {
          const currentTotalWinAmount = currentBets
            .filter(
              (item) =>
                item?.playerExternalId === userInfoStore.playerExternalId &&
                !item.winAmount &&
                !item?.betData?.isManual
            )
            .reduce((acc, curr) => acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2), 0);
          currentTotalWinAmount && marketsStore.setTotalWin(currentTotalWinAmount);
        }
        gameStore.setFinishedOdd(odd);
      }

      if (status === OnHold) {
        gameStore.setIsBetsClosed(true);
      }

      if (mode === GameModes.Fun) {
        applicationStore.setIsDemo(true);
      }

      doBetStore.setBetAmount(initialBetAmount);
      doBetStore.updateAutoCashOutData(
        autoCashOutDataKeys.autoCashOutMaxOdd,
        toFixed(divide(limits.maxWin, initialBetAmount))
      );

      if (currentBets && currentBets.length) {
        betStore.setLiveBets(currentBets);
        if (!applicationStore.isDemo) {
          const userCurrentBets = currentBets.filter(
            (item) =>
              item?.playerExternalId === userInfoStore.playerExternalId &&
              !item.winAmount &&
              item?.betData?.isManual &&
              item.status === BetStatus.Pending
          );
          userCurrentBets.forEach((item) => {
            doBetStore.updateInitialBetData(item);
            doBetStore.updateBetAccepted(true, item.id);
            if (item.freeBetId) {
              const currentFreeBetData = freeBetStore.freeBets.find(
                (freeBet) => freeBet.id === item?.freeBetId
              );
              doBetStore.setBetAmount(currentFreeBetData.amount);
              doBetStore.setFreeBetData(
                currentFreeBetData.minOdd,
                item.freeBetId,
                currentFreeBetData.maxPayout,
                currentFreeBetData?.winType
              );
            }
          });
        }
      }
      resultStore.getLastResult(results);
      gameResultStore.setGameResults(results);
      profileAvatarStore.updateAvatarId(avatarId);
      profileAvatarStore.updateInitialAvatarId(avatarId);
      marketsStore.changeCurrentMarketAmount(
        freeBetStore?.activeFreeBet.id
          ? freeBetStore?.activeFreeBet?.amount
          : limits?.defaultBetAmount || limits?.minBet
      );
      marketsStore.getMarkets(roundInfo.markets);

      roundInfoStore.setRoundInfo(roundInfo);

      gameConfigStore.setLimits(limits);

      if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
        gameStore.setTimerDuration(roundInfo.second);
        const time = Math.floor(Math.abs(roundInfo.second));
        gameStore.setTime(time);
        this.sendWorker(time);
      } else {
        this.sendWorker();
        gameStore.setTimerDuration(roundInfo.duration);
        gameStore.setTime(null);
      }

      loadingStore.updateLoading({ isGameInitialLoaded: true });
      popupStore.close();
      popupStore.toggleReconnect(false);

      userInfoStore.setUserInfo({
        rate,
        balance,
        precision,
        currencyCode,
      });
    }
  }

  handleDoBetResult(data) {
    const { userId } = userInfoStore;
    const { repliedBetData } = betStore;
    const { isSoundOn } = userSettingsStore;
    if (Number(userId) === Number(+data.userId) || repliedBetData) {
      doBetStore.resetIsNextRoundBet();
      if (isSoundOn) {
        soundService.playBetAccepted();
      }
      if (data.freeBetId) {
        freeBetStore.handleFreeBetDoBetResult(data.freeBetId);
      }

      doBetStore.handleDoBetResult(data);
      doBetStore.updateIsDisabledBetSection(false);
    }

    if (data.betData.markets && data.betData.markets.length > 1) {
      data.betData.markets.forEach((el) =>
        betStore.updateLiveBets({
          ...data,
          id: el.id,
          betData: { markets: [el] },
          amount: el.amount,
        })
      );
    } else {
      betStore.updateLiveBets(data);
    }
  }

  handleDoBetResultValidation(data) {
    const { isDemo } = applicationStore;
    const { responseCode } = data;
    const { InsufficientFunds, PlayerLimitExceeded, GeneralError, UnAuthorizedUser } =
      BetResponseCode;

    const isResetAutoBet = [
      InsufficientFunds,
      PlayerLimitExceeded,
      GeneralError,
      ...(!isDemo ? [UnAuthorizedUser] : []),
    ].includes(responseCode);
    const { autoBetData } = doBetStore;
    if (doBetStore.betData.isDisabled && !autoBetData.autoBetCount) {
      doBetStore.updateIsDisabledBetSection(false);
    }
    marketsStore.setIsLoadingBetButton(false);
    marketsStore.setIsLoadingReBetButton(false);

    if (autoBetData.isAutoBet && isResetAutoBet) {
      doBetStore.updateAutoBetData(autoBetDataKeys.isAutoBet, false);
      doBetStore.updateAutoBetData(autoBetDataKeys.autoBetCount, 0);
      doBetStore.updateAutoBetData(autoBetDataKeys.isOpened, false);
      doBetStore.updateIsDisabledBetSection(false);
    }
    notificationStore.setNotificationType({ responseCode });
  }

  betShopGetInitial({ results, roundInfo }) {
    iframeService.sendLoadedState();
    roundInfoStore.setRoundInfo(roundInfo);
    gameResultStore.setGameResults(results);
    betShopResultStore.getLastFiveResult(results);
    betShopResultStore.getTopGoals(roundInfo.roundsWithTopOdds);
    marketsStore.getMarkets(roundInfo.markets);
    gameStore.setRoundId(roundInfo.roundId);
    loadingStore.updateLoading({ isGameInitialLoaded: true });
    if (RoundStatus.Started === roundInfo.status) {
      gameStore.setUpperLayerToggle(true);
    }
    if (RoundStatus.Started === roundInfo.status && roundInfo.second > 0) {
      const time = Math.floor(Math.abs(roundInfo.second));
      // gameStore.setTime(time);
      gameStore.setBetShopTime(time);
      // this.sendWorker(time);
    } else {
      // this.sendWorker();
      gameStore.setBetShopTime(null);
      // gameStore.setTime(null);
    }
  }

  handleRoundStart({ data: { startedRound, results, statistics } }) {
    const { roundId } = startedRound;
    const { totalWin } = marketsStore;
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const isBetShop = +gameId === oddballBetShopGameId;

    this.sendWorker(roundInfoStore.duration);
    if (isBetShop) {
      this.handleBetShopRoundStart(results, startedRound, statistics);
    } else {
      resultStore.getLastResult(results);
      gameStore.setGameState({
        status: RoundStatus.Started,
        isInitialLoaded: false,
      });

      if (totalWin && !betStore?.repliedBetData && !isBetShop) {
        gameLiveConnection.getBalance();
      }
      doBetStore.setWinAmount('');
      gameStore.setFinishedOdd(null);
      !marketsStore.marketBetData.isMarketNextRoundBet &&
        marketsStore.updateIsDisabledMarketBetButton(false);
      // marketsStore.setIsDisabledMarketsSection(false);
      marketsStore.setTotalWin('');
      gameResultStore.setGameResults(results);
      betStore.setLiveBets([]);

      marketsStore.setBetsMarketData(null);
      gameActiveVideoStore.resetActiveGameVideo();
      gameActiveVideoStore.setGameStart(true);
      roundInfoStore.setRoundId(roundId);
      doBetStore.handleOnRoundStart();
    }
    freeBetStore.handleFreeBetEndedData();
  }

  handleBetShopRoundStart(results, startedRound, statistics) {
    betShopResultStore.getLastFiveResult(results);
    betShopResultStore.getTopGoals(statistics.roundsWithTopOdds);
    gameStore.setRoundId(startedRound.roundId);
    gameStore.setBetShopTime(startedRound.duration);
    gameStore.setUpperLayerToggle(true);
    gameSceneStore.setGameFinish(false);
  }

  handleResultReport({ data: { resultReportData } }) {
    const { bets, odd } = resultReportData;
    const { isOpened, autoCashOutOdd } = doBetStore.autoCashOutData;
    const { isFirst } = doBetStore.activeMaxPayoutPopData;
    const {
      isBetAccepted,
      betId,
      isDisabled,
      amount,
      freeBetMaxPayout = null,
      freeBetId,
      freeBetType,
    } = doBetStore.betData;
    const { isAutoBet, autoBetCount } = doBetStore.autoBetData;
    const { isDemo } = applicationStore;
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const isBetShop = +gameId === oddballBetShopGameId;

    if (isBetShop) {
      reportsStore.setCurrentOdd(resultReportData.odd);
      gameSceneStore.setGameMain(true);
    } else {
      const {
        marketBetData: { isMarketNextRoundBet },
        isDisabledMarketsSection,
      } = marketsStore;

      if (isDisabledMarketsSection && !isMarketNextRoundBet) {
        marketsStore.setIsDisabledMarketsSection(false);
      }
      gameStore.setGameState({
        status: '',
        isInitialLoaded: false,
      });

      if (isDemo) {
        doBetStore.updateTotalBetsAmount(0, true);
      }
      const freeBetAmount =
        freeBetId && freeBetType === FreeBetWinningType.Win
          ? toFixed(
              minus(Number(toFixed(multiply(odd || 0, Number(amount)), 2)), Number(amount) || 0) ||
                0,
              2
            )
          : toFixed(multiply(odd || 0, Number(amount || 0)) || 0, 2);
      if (freeBetId && isBetAccepted && freeBetMaxPayout && freeBetAmount > freeBetMaxPayout) {
        doBetStore.setActiveMaxPayoutPopUpData(true, freeBetMaxPayout, isFirst);
      }
      gameActiveVideoStore.setGameProcess(true);
      gameStore.setOddData(resultReportData.odd);
      marketsStore.updateIsDisabledMarketBetButton(false);

      if (!gameStore.isBetsClosed) {
        gameStore.setIsBetsClosed(true);
      }

      if (isBetAccepted && gameStore.isBetsClosed && isDisabled) {
        doBetStore.updateIsDisabledBetSection(false);
      }

      if (isDemo && doBetStore.betData.isDisabled && !isAutoBet) {
        doBetStore.updateIsDisabledBetSection(false);
      }

      if (isDemo && isOpened && gameStore.oddData === Number(autoCashOutOdd) && isBetAccepted) {
        doBetStore.sendCashOut(betId);
      }
      if (bets?.length) {
        const currentBets = betStore.liveBets;
        const winData = bets.filter(
          (item) => userInfoStore.playerExternalId === item.playerExternalId && !isDemo
        );

        if (winData?.length) {
          doBetStore.setWinAmount(winData?.[0]?.winAmount);
          setTimeout(gameLiveConnection.getBalance, 500);
        }

        winData.forEach((itemWindData) => {
          doBetStore.setWinData(itemWindData);
          freeBetStore.freeBetTotalWinCalculator(itemWindData);
          if (autoCashOutOdd && isOpened && isAutoBet && autoBetCount) {
            doBetStore.updateIsDisabledBetSection(true);
          }
          if (autoCashOutOdd && isOpened && isAutoBet && !autoBetCount) {
            doBetStore.resetAutoBet();
          }
        });

        const sortWiningPendingLivBets = currentBets.map((bet) => {
          const res = bets.find((item) => {
            return item.id === bet.id;
          });
          return res || bet;
        });
        betStore.setLiveBets(sortWiningPendingLivBets);
      }
    }
  }

  handleRoundFin({ data }) {
    const {
      bets,
      finishedRound: { roundData },
    } = data;
    const { isSoundOn } = userSettingsStore;

    const {
      autoBetData: { isAutoBet, autoBetCount },
    } = doBetStore;
    const odd = typeof roundData === 'string' ? JSON.parse(roundData).odd : roundData.odd;
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const isBetShop = +gameId === oddballBetShopGameId;

    if (isBetShop) {
      let parsedRoundData = roundData;
      if (typeof parsedRoundData === 'string') {
        parsedRoundData = JSON.parse(roundData);
      }

      reportsStore.setCurrentOdd(parsedRoundData.odd);
      setTimeout(() => {
        gameSceneStore.setGameFinish(true);
        reportsStore.setFinishOdd(parsedRoundData.odd);
      }, 1000);
    } else {
      gameStore.setGameState({
        status: '',
        isInitialLoaded: false,
      });
      doBetStore.resetActiveMaxPayoutPopUpData(true);
      marketsStore.updateIsDisabledMarketBetButton(true);

      isSoundOn && soundService.play(soundTypes.finish);

      if (isAutoBet && !autoBetCount) {
        doBetStore.resetAutoBet();
      }
      // if (isDemo) {
      //   const { liveBets } = betStore;
      //
      //   doBetStore.updateIsDisabledBetSection(true);
      //
      //   const demoBetsData = filteredDemoBetsWinData(liveBets);
      //
      //   const realPlayersData = liveBets?.filter(
      //     (item) => item?.playerExternalId || (!item?.playerExternalId && !item?.betData?.markets)
      //   );
      //   demoBets = liveBets
      //     ?.filter((item) => !item.playerExternalId)
      //     ?.map((item) => {
      //       if (item?.betData?.markets) {
      //         return changeMarketsBetsData(item, odd);
      //       }
      //       if (item.status !== BetStatus.Win) {
      //         return {
      //           ...item,
      //           winAmount: 0,
      //           status: BetStatus.Lose,
      //         };
      //       }
      //       return item;
      //     });
      //   const demoMarketsBetsData = liveBets
      //     .filter((betData) => {
      //       return !betData?.playerExternalId && betData?.betData?.markets;
      //     })
      //     .map((item) => changeMarketsBetsData(item, odd));
      //
      //   const domoTotalWinData = demoMarketsBetsData.reduce(
      //     (acc, curr) => acc + parseNumberByPrecision(Number(curr?.amount), Number(curr?.odd), 2),
      //     0
      //   );
      //
      //   if (domoTotalWinData && domoTotalWinData > maxWin) {
      //     notificationStore.setNotificationType({ responseCode: BetResponseCode.MaxWinPayout });
      //   }
      //   userInfoStore.setDemoBalance(userInfoStore.balance + Number(domoTotalWinData));
      //   betStore.setLiveBets([...demoBets, ...realPlayersData]);
      //   marketsStore.setTotalWin(domoTotalWinData > maxWin ? maxWin : domoTotalWinData);
      //   betStore.updateDemoBetHistory([...demoBetsData, ...demoMarketsBetsData]);
      //   doBetStore.updateTotalBetsAmount(0, true);
      // }

      if (bets?.length) {
        const currentTotalWinData = bets
          ?.filter(
            (item) =>
              +item?.userId === +userInfoStore?.userId &&
              item.status === BetStatus.Win &&
              item?.additionalParams?.markets
          )
          ?.reduce((acc, curr) => {
            freeBetStore.freeBetTotalWinCalculator(curr);
            return acc + parseNumberByPrecision(Number(curr?.winAmount), 1, 2);
          }, 0);
        marketsStore.setTotalWin(currentTotalWinData);
        if (currentTotalWinData) {
          soundService.playWin();
        }
      }

      const finishLiveBets = parseDataFromLiveBets(bets);
      betStore.setLiveBets(finishLiveBets);
      gameStore.setTimerDuration(roundInfoStore.duration);
      // marketsStore.setIsDisabledMarketsSection(true);
      marketsStore.resetDisabledMarkets();
      gameStore.setFinishedOdd(odd);
      gameStore.setIsShowGameResultPopup(true);

      setTimeout(() => {
        gameStore.setIsShowGameResultPopup(false);
      }, 1200);

      doBetStore.handleClearRoundFin();
      !doBetStore.betData.isNextRoundBet && doBetStore.updateIsDisabledBetSection(true);
      gameActiveVideoStore.setGameFinish(true);
      gameStore.setIsBetsClosed(false);

      const myBets = bets.filter((item) => +item.userId === +userInfoStore.userId);
      doBetStore.detectAndSetReBetData(myBets);
      // doBetStore.detectAndSetReBetData(myBets);
    }
  }

  handleBetsClosed() {
    const { isSoundOn } = userSettingsStore;
    const { isDemo } = applicationStore;
    const { [DefaultQueryParams.GameId]: gameId } = parseQuery(window.location.search);
    const isBetShop = +gameId === oddballBetShopGameId;

    if (isBetShop) {
      gameStore.setBetShopTime(null);
      gameStore.setUpperLayerToggle(false);
      gameSceneStore.setGameStart(true);
    } else {
      gameStore.setGameState({
        status: '',
        isInitialLoaded: false,
      });
      marketsStore.setIsLoadingBetButton(false);
      marketsStore.setIsLoadingReBetButton(false);
      marketsStore.updateIsDisabledMarketBetButton(true);
      marketsStore.setIsDisabledMarketsSection(true);
      gameActiveVideoStore.setGameKick(true);
      gameStore.setIsBetsClosed(true);

      if (isDemo) {
        doBetStore.updateIsDisabledBetSection(true);
      }

      // soundService.stopAll();
      isSoundOn && soundService.play(soundTypes.start);
    }
  }

  handleCashOutResult(cashOutData) {
    const bets = betStore.liveBets;
    const { isAutoBet, autoBetCount } = doBetStore.autoBetData;
    const { isDemo } = applicationStore;
    const { repliedBetData } = betStore;
    const { userId: currentUserId } = userInfoStore;
    const { id } = cashOutData;

    const cashOutLivBets = bets.map((bet) => {
      if (bet?.id === id) {
        return cashOutData;
      }
      return bet;
    });

    freeBetStore.freeBetTotalWinCalculator(cashOutData);
    if (isAutoBet && autoBetCount && (cashOutData.userId === Number(currentUserId) || isDemo)) {
      doBetStore.updateIsDisabledBetSection(true);
    }
    doBetStore.resetActiveMaxPayoutPopUpData(true);
    gameStore.setGameState({
      status: '',
      isInitialLoaded: false,
    });

    betStore.setLiveBets(cashOutLivBets);
    if (cashOutData.userId === Number(currentUserId) || repliedBetData) {
      doBetStore.setWinData(cashOutData);
    }
  }

  countDown({ data: time }) {
    gameStore.setTime(time);
  }

  handleReconnecting(error) {
    if (error && error.message) {
      popupStore.toggleReconnect();
    } else {
      GameService.ReconnectCount += 1;
      popupStore.toggleReconnect();
    }
  }

  handleReconnected() {
    GameService.ReconnectCount = 0;
    gameLiveConnection.sendGameGetInitialState(false);
  }

  handleConnectionError(e) {
    console.error(e, 'error onClose');
    if (GameService.ReconnectCount) {
      popupStore.onConnectionError(true);
    }
  }

  socketRecursiveReconnecting() {
    try {
      liveConnection
        .restartGameConnection()
        .then((connectionData) => {
          popupStore.connectionError && popupStore.onConnectionError();
          liveConnection.connectionInitCallback(connectionData);
          clearTimeout(timeOutId);
        })
        .catch(() => {
          timeOutId = setTimeout(() => {
            this.socketRecursiveReconnecting();
          }, 4000);
        });
    } catch (err) {
      popupStore.onConnectionError(true);
    }
  }

  sendWorker(timerValue) {
    if (timerValue) {
      worker.addEventListener('message', this.countDown);
      worker.postMessage(timerValue);
    } else {
      worker.removeEventListener('message', this.countDown);
    }
  }
}

export const gameService = new GameService();
